import React, { useState, useMemo } from 'react';
import type { BreadthSummary } from '../types';

interface BreadthChartProps {
  breadth: BreadthSummary;
}

type TimeframeWindow = '60' | '120' | '250';

interface TimeframeSeriesConfig {
  labelZh: string;
  labelEn: string;
  startDateLabel: string;
  midDateLabel: string;
  endDateLabel: string;
  milestone1: { xPct: number; xSvg: number; text: string };
  milestone2: { xPct: number; xSvg: number; text: string };
  // Historical normalized ratios (buyPct, waitPct) for the first 6 points; point 7 is live current breadth
  historyRatios: Array<{ buy: number; wait: number }>;
  taiexY: number[];
}

const TIMEFRAME_CONFIGS: Record<TimeframeWindow, TimeframeSeriesConfig> = {
  '60': {
    labelZh: '全市場60日循環深度',
    labelEn: '(60-Day Market Breadth Stacked Area)',
    startDateLabel: '2026-07-22',
    midDateLabel: '2026-09-02',
    endDateLabel: '2026-10-15',
    milestone1: { xPct: 28, xSvg: 200, text: '08/05 融資清洗低點 (B3擴增)' },
    milestone2: { xPct: 64, xSvg: 455, text: '09/18 降息預期 A1→A2 點火' },
    historyRatios: [
      { buy: 0.42, wait: 0.34 },
      { buy: 0.56, wait: 0.28 },
      { buy: 0.64, wait: 0.24 },
      { buy: 0.58, wait: 0.27 },
      { buy: 0.63, wait: 0.25 },
      { buy: 0.66, wait: 0.23 },
    ],
    taiexY: [135, 162, 128, 108, 92, 74, 54],
  },
  '120': {
    labelZh: '全市場120日循環深度',
    labelEn: '(120-Day Market Breadth Stacked Area)',
    startDateLabel: '2026-04-20',
    midDateLabel: '2026-07-15',
    endDateLabel: '2026-10-15',
    milestone1: { xPct: 33, xSvg: 240, text: '06/04 回測季線支撐 (A1吸籌)' },
    milestone2: { xPct: 66, xSvg: 480, text: '08/19 半導體領軍 A2 放量' },
    historyRatios: [
      { buy: 0.31, wait: 0.42 },
      { buy: 0.38, wait: 0.38 },
      { buy: 0.49, wait: 0.31 },
      { buy: 0.44, wait: 0.35 },
      { buy: 0.57, wait: 0.28 },
      { buy: 0.63, wait: 0.25 },
    ],
    taiexY: [175, 160, 142, 148, 112, 82, 54],
  },
  '250': {
    labelZh: '全市場250日循環深度',
    labelEn: '(250-Day Market Breadth Stacked Area)',
    startDateLabel: '2025-10-16',
    midDateLabel: '2026-04-15',
    endDateLabel: '2026-10-15',
    milestone1: { xPct: 26, xSvg: 190, text: '01/15 年線乖離修正 (B2→B3)' },
    milestone2: { xPct: 62, xSvg: 445, text: '05/20 全市場重返多頭 (BULL_ZONE)' },
    historyRatios: [
      { buy: 0.22, wait: 0.36 },
      { buy: 0.48, wait: 0.34 },
      { buy: 0.35, wait: 0.39 },
      { buy: 0.52, wait: 0.30 },
      { buy: 0.60, wait: 0.26 },
      { buy: 0.65, wait: 0.24 },
    ],
    taiexY: [188, 152, 172, 134, 98, 76, 54],
  },
};

export const BreadthChart: React.FC<BreadthChartProps> = ({ breadth }) => {
  const [timeframe, setTimeframe] = useState<TimeframeWindow>('120');

  const buyPct = breadth.pct_buy_zone * 100;
  const waitPct = breadth.pct_wait_zone * 100;
  const sellPct = breadth.pct_sell_zone * 100;

  const buyCount = (breadth.phase_counts.B3 || 0) + (breadth.phase_counts.A1 || 0);
  const waitCount = (breadth.phase_counts.A2 || 0) + (breadth.phase_counts.B2 || 0);
  const sellCount = (breadth.phase_counts.A3 || 0) + (breadth.phase_counts.B1 || 0);

  const cfg = TIMEFRAME_CONFIGS[timeframe];

  // Dynamically compute SVG polygon points for the selected timeframe + live right-edge breadth
  const { sellPolygon, waitPolygon, buyPolygon, taiexPolyline } = useMemo(() => {
    const xCoords = [0, 115, 230, 350, 470, 585, 700];
    const allRatios = [
      ...cfg.historyRatios,
      { buy: breadth.pct_buy_zone, wait: breadth.pct_wait_zone },
    ];

    // SVG height is 200 (y=18 is top 100%, y=200 is bottom 0%)
    const chartTop = 18;
    const chartBottom = 200;
    const chartHeight = chartBottom - chartTop;

    const sellTopPts: string[] = [];
    const waitTopPts: string[] = [];
    const buyTopPts: string[] = [];
    const taiexPts: string[] = [];

    xCoords.forEach((x, idx) => {
      const r = allRatios[idx];
      const buyH = Math.round(r.buy * chartHeight);
      const waitH = Math.round(r.wait * chartHeight);

      const yBuyTop = chartBottom - buyH;
      const yWaitTop = Math.max(chartTop + 8, yBuyTop - waitH);
      const ySellTop = chartTop + (idx % 2 === 0 ? 4 : 7);

      sellTopPts.push(`${x},${ySellTop}`);
      waitTopPts.push(`${x},${yWaitTop}`);
      buyTopPts.push(`${x},${yBuyTop}`);
      taiexPts.push(`${x},${cfg.taiexY[idx]}`);
    });

    return {
      sellPolygon: `${sellTopPts.join(' ')} 700,200 0,200`,
      waitPolygon: `${waitTopPts.join(' ')} 700,200 0,200`,
      buyPolygon: `${buyTopPts.join(' ')} 700,200 0,200`,
      taiexPolyline: taiexPts.join(' '),
    };
  }, [cfg, breadth.pct_buy_zone, breadth.pct_wait_zone]);

  return (
    <div className="stitch-card rounded-lg p-3.5 flex flex-col h-full select-none">
      {/* Header & Controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-terminal-cyan animate-pulse"></span>
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3
              data-testid="breadth-chart-title"
              className="text-sm md:text-[15px] font-semibold text-slate-100 tracking-tight"
            >
              {cfg.labelZh}
            </h3>
            <span className="text-[11px] font-mono font-medium text-sky-400/85">
              {cfg.labelEn}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1 bg-[#0b0f17] p-0.5 rounded border border-[#182232] text-[10px] font-mono">
          {(['60', '120', '250'] as const).map((tf) => (
            <button
              key={tf}
              type="button"
              data-testid={`breadth-tf-${tf}`}
              onClick={() => setTimeframe(tf)}
              className={`px-2 py-0.5 rounded transition-all cursor-pointer ${
                timeframe === tf
                  ? 'bg-primary-container text-white font-bold shadow-sm'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              {tf}D
            </button>
          ))}
        </div>
      </div>

      {/* Zone Legend + TAIEX Curve Legend */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] font-mono mb-2">
        <div className="flex flex-wrap items-center gap-3.5">
          <div className="flex items-center gap-1.5 text-sky-300 font-medium">
            <div className="w-2.5 h-2.5 rounded-xs bg-primary-container"></div>
            <span>底層藍：買進吸籌區 B3+A1 ({buyPct.toFixed(1)}%)</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-200 font-medium">
            <div className="w-2.5 h-2.5 rounded-xs bg-slate-500"></div>
            <span>中層灰：隨波等待區 A2+B2 ({waitPct.toFixed(1)}%)</span>
          </div>
          <div className="flex items-center gap-1.5 text-emerald-300 font-medium">
            <div className="w-2.5 h-2.5 rounded-xs bg-market-up"></div>
            <span>上層綠：過熱賣出區 A3+B1 ({sellPct.toFixed(1)}%)</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-terminal-cyan font-semibold">
          <span className="inline-block w-4 h-0.5 bg-terminal-cyan"></span>
          <span>青色曲線：加權指數 (TAIEX) 走勢</span>
        </div>
      </div>

      {/* Stacked Area SVG Visualization */}
      <div className="relative flex-1 w-full min-h-[210px] bg-[#0b0f17] rounded-md border border-[#182232] p-1 overflow-hidden">
        <svg
          data-testid={`breadth-svg-${timeframe}`}
          viewBox="0 0 700 200"
          className="w-full h-full"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="areaSellGreen" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#047857" stopOpacity="0.2" />
            </linearGradient>
            <linearGradient id="areaWaitSlate" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#64748b" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#334155" stopOpacity="0.25" />
            </linearGradient>
            <linearGradient id="areaBuyBlue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2563eb" stopOpacity="0.65" />
              <stop offset="100%" stopColor="#1e3a8a" stopOpacity="0.25" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          <line x1="0" y1="40" x2="700" y2="40" stroke="#182232" strokeDasharray="2 2" strokeWidth="1" />
          <line x1="0" y1="80" x2="700" y2="80" stroke="#182232" strokeDasharray="2 2" strokeWidth="1" />
          <line x1="0" y1="120" x2="700" y2="120" stroke="#182232" strokeDasharray="2 2" strokeWidth="1" />
          <line x1="0" y1="160" x2="700" y2="160" stroke="#182232" strokeDasharray="2 2" strokeWidth="1" />

          {/* Area 3: Top Sell Area (Green) */}
          <polygon data-testid="breadth-poly-sell" points={sellPolygon} fill="url(#areaSellGreen)" />

          {/* Area 2: Middle Wait Area (Slate) */}
          <polygon data-testid="breadth-poly-wait" points={waitPolygon} fill="url(#areaWaitSlate)" />

          {/* Area 1: Bottom Buy Area (Cyber Blue) */}
          <polygon data-testid="breadth-poly-buy" points={buyPolygon} fill="url(#areaBuyBlue)" />

          {/* TAIEX Index Trajectory Line (Neon Cyan) */}
          <polyline
            data-testid="breadth-taiex-line"
            points={taiexPolyline}
            fill="none"
            stroke="#38bdf8"
            strokeWidth="2.5"
            strokeLinecap="round"
          />

          {/* Historic Event Milestones Vertical Indicators */}
          <line
            x1={cfg.milestone1.xSvg}
            y1="20"
            x2={cfg.milestone1.xSvg}
            y2="200"
            stroke="#2563eb"
            strokeWidth="1.5"
            strokeDasharray="3 3"
          />
          <line
            x1={cfg.milestone2.xSvg}
            y1="20"
            x2={cfg.milestone2.xSvg}
            y2="200"
            stroke="#38bdf8"
            strokeWidth="1.5"
            strokeDasharray="3 3"
          />
          <line x1="695" y1="20" x2="695" y2="200" stroke="#38bdf8" strokeWidth="2" />
        </svg>

        {/* Dynamic Milestone Badges per Timeframe */}
        <div
          data-testid="breadth-milestone-1"
          style={{ left: `${cfg.milestone1.xPct}%` }}
          className="absolute top-2 -translate-x-1/2 px-2 py-0.5 rounded bg-[#0b0f17]/95 border border-blue-500/40 text-[10px] font-mono text-terminal-cyan shadow whitespace-nowrap"
        >
          {cfg.milestone1.text}
        </div>
        <div
          data-testid="breadth-milestone-2"
          style={{ left: `${cfg.milestone2.xPct}%` }}
          className="absolute top-8 -translate-x-1/2 px-2 py-0.5 rounded bg-[#0b0f17]/95 border border-[#283548] text-[10px] font-mono text-slate-200 shadow whitespace-nowrap"
        >
          {cfg.milestone2.text}
        </div>
        <div className="absolute right-2 top-2 px-2.5 py-0.5 rounded bg-[#1c2230] border border-primary-container text-[10px] font-mono text-white font-semibold shadow">
          當前 ({cfg.endDateLabel}): {(buyPct + waitPct).toFixed(1)}% 位於買進與等待區
        </div>
      </div>

      {/* X-Axis Date Labels */}
      <div className="flex justify-between items-center text-[10px] font-mono text-slate-400 px-1 mt-1">
        <span>起點: {cfg.startDateLabel}</span>
        <span>中段: {cfg.midDateLabel}</span>
        <span className="text-sky-300 font-semibold">最新結算: {cfg.endDateLabel} ({timeframe}日視窗)</span>
      </div>

      {/* 3-Zone Horizontal Summary Breakdown Bar */}
      <div className="w-full flex h-2.5 rounded overflow-hidden mt-2 bg-[#0b0f17]">
        <div
          className="bg-primary-container h-full transition-all duration-300"
          style={{ width: `${buyPct}%` }}
          title={`做多買進區: ${buyPct.toFixed(1)}%`}
        />
        <div
          className="bg-slate-600 h-full transition-all duration-300"
          style={{ width: `${waitPct}%` }}
          title={`隨波等待區: ${waitPct.toFixed(1)}%`}
        />
        <div
          className="bg-market-up h-full transition-all duration-300"
          style={{ width: `${sellPct}%` }}
          title={`獲利賣出區: ${sellPct.toFixed(1)}%`}
        />
      </div>

      <div className="flex flex-wrap justify-between items-center text-[11px] font-mono text-slate-300 mt-1.5 gap-2">
        <span>
          <strong className="text-sky-300 font-semibold">買進吸籌區 (B3+A1):</strong>{' '}
          <span className="text-slate-100 font-bold">{buyPct.toFixed(1)}%</span> ({buyCount}檔)
        </span>
        <span>
          <strong className="text-slate-200 font-semibold">隨波等待區 (A2+B2):</strong>{' '}
          <span className="text-slate-100 font-bold">{waitPct.toFixed(1)}%</span> ({waitCount}檔)
        </span>
        <span>
          <strong className="text-emerald-300 font-semibold">逢高撤退區 (A3+B1):</strong>{' '}
          <span className="text-slate-100 font-bold">{sellPct.toFixed(1)}%</span> ({sellCount}檔)
        </span>
      </div>
    </div>
  );
};
